import { useQuery } from '@tanstack/react-query';

import { listRules } from '@/api/rules';
import { listRuleSets } from '@/api/rule_sets';
import { Badge } from '@/components/ui/badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { WikiLayout, WikiSection, type WikiTocEntry } from '@/components/wiki-layout';
import {
  combinatorOf,
  describeFormula,
  describePredicate,
  humanField,
  isPredicate,
  predicateTriple,
} from '@/lib/rules-prose';
import { useAuth } from '../auth/AuthContext';
import type {
  FindingOutcome,
  NestedTrigger,
  RuleOut,
  RuleRecoveryFormula,
  RuleSetOut,
  RuleTriggerNode,
} from '@/api/types';

/** Rule of record for the Now Vertical engagement. The wiki shows only
 *  the rules referenced by this set's latest version. */
const ACTIVE_RULE_SET = 'nvg-baseline';

/** Wiki page for the rules in the active rule set.
 *
 *  Layout per rule (top-to-bottom): badges → title → summary →
 *  TRIGGER (structured predicate tree, monospace) → RECOVERY
 *  (formula as an equation + variable legend). The three blocks fit
 *  one viewport for any rule in the catalog.
 *
 *  Why structured-only: a practitioner judging "should this rule have
 *  fired?" needs the predicates and the formula unambiguously, not a
 *  prose restatement that risks drifting from the YAML. The summary
 *  is the only narrative; everything below it reads off the rule data. */
export function RulesPage() {
  const { isSignedIn, userKey, getIdToken } = useAuth();

  const rulesQ = useQuery({
    queryKey: ['rules', userKey],
    enabled: isSignedIn,
    queryFn: async () => listRules((await getIdToken()) ?? ''),
  });
  const setsQ = useQuery({
    queryKey: ['rule-sets', userKey],
    enabled: isSignedIn,
    queryFn: async () => listRuleSets((await getIdToken()) ?? ''),
  });

  if (!isSignedIn) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Not signed in</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (rulesQ.isPending || setsQ.isPending) return <p className="text-brand-slate">Loading rules…</p>;
  if (rulesQ.isError || setsQ.isError) {
    console.error('listRules / listRuleSets failed:', rulesQ.error ?? setsQ.error);
    return (
      <Card className="max-w-xl border-destructive/30">
        <CardHeader>
          <CardTitle>Couldn't load rules</CardTitle>
          <CardDescription>
            Something went wrong. Try refreshing. Details in the browser console.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const allRules = rulesQ.data.items;
  const activeSet = setsQ.data.items.find((s) => s.id === ACTIVE_RULE_SET);
  const rules = orderedRulesFor(activeSet, allRules);

  const toc: WikiTocEntry[] = rules.map((rule) => ({
    id: rule.id,
    label: rule.title,
    tag: rule.aa_code,
  }));

  const description = activeSet
    ? `The ${rules.length} rules the engine evaluates on every Now Vertical audit run (${activeSet.id} v${activeSet.version}).`
    : 'Active rule set not found.';

  return (
    <WikiLayout title="Recovery rules" description={description} toc={toc}>
      {rules.length === 0 ? (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>No rules</CardTitle>
            <CardDescription>
              The {ACTIVE_RULE_SET} rule set has no resolvable rules. Check the server logs.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-6">
          {rules.map((rule) => (
            <RuleEntry key={rule.id} rule={rule} />
          ))}
        </div>
      )}
    </WikiLayout>
  );
}

/** Resolve the rule-set's rule-id list against the rule catalog, in the
 *  same order the set lists them (rule-set authors order intentionally —
 *  by family, by priority — and the wiki should mirror that). Missing
 *  ids are dropped silently; the catalog → rule-set bind happens
 *  server-side, so a missing rule here means a server issue worth
 *  surfacing in logs but not blocking the page. */
function orderedRulesFor(set: RuleSetOut | undefined, allRules: RuleOut[]): RuleOut[] {
  if (!set) return [];
  const byId = new Map(allRules.map((r) => [r.id, r]));
  const out: RuleOut[] = [];
  for (const ref of set.rules) {
    const r = byId.get(ref.id);
    if (r) out.push(r);
  }
  return out;
}

function RuleEntry({ rule }: { rule: RuleOut }) {
  return (
    <article
      id={rule.id}
      className="grid scroll-mt-8 gap-5 rounded-lg border border-border bg-card px-5 py-5 shadow-sm transition-colors hover:border-brand-royal/30 sm:px-6 sm:py-6"
    >
      <header className="grid gap-2 border-b border-border/60 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="font-mono">
            {rule.aa_code}
          </Badge>
          <OutcomeBadge outcome={rule.outcome} />
          <a
            href={`#${rule.id}`}
            className="ml-auto font-mono text-xs text-brand-slate/70 transition-colors hover:text-brand-royal"
            title="Permalink to this rule"
          >
            {rule.id}@v{rule.version}
          </a>
        </div>
        <h3 className="text-lg font-semibold tracking-tight text-brand-navy">{rule.title}</h3>
        <p className="text-sm leading-relaxed text-brand-slate">{firstSentence(rule.summary)}</p>
      </header>

      <WikiSection title="Trigger">
        <TriggerTree root={rule.trigger.all_of as RuleTriggerNode[]} />
      </WikiSection>

      <WikiSection title="Recovery">
        <RecoveryFormula formula={rule.recovery_formula} />
      </WikiSection>
    </article>
  );
}

/** Render the rule's trigger as an indented tree. The top-level node is
 *  always an `ALL of` (Pydantic shape), so we wrap the children list in
 *  a labeled group from the start. */
function TriggerTree({ root }: { root: RuleTriggerNode[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border bg-secondary/30 p-3 font-mono text-xs leading-6 text-brand-navy">
      <CombinatorBlock kind="all" nodes={root} depth={0} />
    </div>
  );
}

/** One indentation level: the kind label (`ALL of` / `ANY of` / `NOT`)
 *  on its own line, then each child rendered at depth+1. */
function CombinatorBlock({
  kind,
  nodes,
  depth,
}: {
  kind: 'all' | 'any' | 'not';
  nodes: RuleTriggerNode[];
  depth: number;
}) {
  const label = kind === 'all' ? 'ALL of' : kind === 'any' ? 'ANY of' : 'NOT';
  return (
    <div style={{ paddingLeft: `${depth * 1.25}rem` }} className="grid gap-1">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-royal">
        {label}
      </div>
      {nodes.map((node, i) => (
        <TriggerNodeRow key={`${depth}-${i}`} node={node} depth={depth + 1} />
      ))}
    </div>
  );
}

function TriggerNodeRow({ node, depth }: { node: RuleTriggerNode; depth: number }) {
  if (isPredicate(node)) {
    const { field, op, value } = predicateTriple(node);
    // Render as a 3-column row so fields/ops/values line up across the tree.
    return (
      <div
        style={{ paddingLeft: `${depth * 1.25}rem` }}
        className="grid grid-cols-[minmax(0,auto)_minmax(0,auto)_minmax(0,1fr)] items-baseline gap-x-3"
        title={describePredicate(node)}
      >
        <span className="text-brand-navy">{field}</span>
        <span className="text-brand-slate">{op}</span>
        <span className="text-brand-royal break-words">{value}</span>
      </div>
    );
  }
  const { kind, children } = combinatorOf(node as NestedTrigger);
  return <CombinatorBlock kind={kind} nodes={children} depth={depth} />;
}

/** Render the recovery formula as a typeset equation + a "where" legend
 *  that names each variable. Pattern lifted from CloudWatch alarm bodies
 *  / Splunk eval lines: the formula is the rule, so render it directly. */
function RecoveryFormula({ formula }: { formula: RuleRecoveryFormula }) {
  if (formula.op === 'rate_lookup_factor') {
    return (
      <div className="grid gap-2 rounded-md border border-border bg-secondary/30 p-3 font-mono text-xs leading-6 text-brand-navy">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-brand-slate">recovery</span>
          <span className="text-brand-slate">=</span>
          <span className="text-brand-navy">{formula.base_field}</span>
          <span className="text-brand-slate">×</span>
          <span className="text-brand-royal">r ÷ (1 + r)</span>
        </div>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-[11px] text-brand-slate">
          <dt className="text-brand-navy">{formula.base_field}</dt>
          <dd>{humanField(formula.base_field).toLowerCase()}</dd>
          <dt className="text-brand-royal">r</dt>
          <dd>
            {formula.rate_type.toUpperCase()} rate in force on the{' '}
            {humanField(formula.as_of_field).toLowerCase()} for{' '}
            {humanField(formula.jurisdiction_field).toLowerCase()}
          </dd>
        </dl>
        <p className="font-sans text-xs italic text-brand-slate">{describeFormula(formula)}</p>
      </div>
    );
  }

  if (formula.op !== 'factor') {
    return (
      <p className="text-sm text-brand-slate">
        Recovery formula uses an operation the wiki doesn't yet render.
      </p>
    );
  }

  if (formula.numerator === 0) {
    return (
      <div className="rounded-md border border-amber-300/40 bg-amber-50/60 px-3 py-2 text-sm text-brand-slate">
        <span className="font-mono text-xs">recovery = 0</span> — review-only flag; no claim is
        made.
      </div>
    );
  }

  const baseLabel = humanField(formula.base_field).toLowerCase();
  const ratePct = ((formula.numerator / formula.denominator) * 100).toFixed(3).replace(/\.?0+$/, '');

  return (
    <div className="grid gap-2 rounded-md border border-border bg-secondary/30 p-3 font-mono text-xs leading-6 text-brand-navy">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-brand-slate">recovery</span>
        <span className="text-brand-slate">=</span>
        <span className="text-brand-navy">{formula.base_field}</span>
        <span className="text-brand-slate">×</span>
        <span className="text-brand-royal">
          {formula.numerator}/{formula.denominator}
        </span>
      </div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-[11px] text-brand-slate">
        <dt className="text-brand-navy">{formula.base_field}</dt>
        <dd>{baseLabel}</dd>
        <dt className="text-brand-royal">
          {formula.numerator}/{formula.denominator}
        </dt>
        <dd>embedded-tax factor (≈ {ratePct}%)</dd>
      </dl>
      <p className="font-sans text-xs italic text-brand-slate">{describeFormula(formula)}</p>
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: FindingOutcome }) {
  if (outcome === 'recovery') {
    return <Badge className="border-transparent bg-emerald-600 text-white">recovery</Badge>;
  }
  return <Badge className="border-transparent bg-amber-600 text-white">compliance risk</Badge>;
}

/** Cut to the first sentence — rule summaries often run 2-3 sentences
 *  in the YAML; the wiki's header only needs the headline. The full
 *  summary still flows through the API for callers that want it. */
function firstSentence(text: string): string {
  const trimmed = text.trim();
  // Match up to the first sentence terminator followed by whitespace.
  const m = trimmed.match(/^(.+?[.!?])(?:\s|$)/);
  return m ? m[1] : trimmed;
}
